import { dateFormatFormKit, dateFormatPrettyFullYear, dateFormatPrettyMinimizedYear, dateTimeFormatFormKit } from '@shared/constants/date-time';
import dayjs, { type Dayjs, type OpUnitType, extend } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isToday from 'dayjs/plugin/isToday';
import isTomorrow from 'dayjs/plugin/isTomorrow';
import isYesterday from 'dayjs/plugin/isYesterday';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';

extend(customParseFormat);
extend(isSameOrBefore);
extend(isSameOrAfter);
extend(weekday);
extend(weekOfYear);
extend(isBetween);
extend(isToday);
extend(isTomorrow);
extend(isYesterday);

export { type Dayjs };

/**
 * The name of this package does not make much sense
 * It used to be for timezone stuff but is not used to make sure we only use a single dayjs instance
 * The time-zone is always in the context of the venue
 * FormKit timezones and taken out and dayjs just uses the timezone of the user while calculating and takes it off again
 * Whenever you use a time in FormKit please use this formatting: .format(dateTimeFormatFormKit)
 */
export const dayjsNow = (): Dayjs =>
    dayjs();

export const dayjsObject = (time?: string | Dayjs | Date): Dayjs =>
    dayjs(time);

export const dayjsFormatter = (
    time?: string | Dayjs | Date,
    format?: string,
    strict?: boolean,
): Dayjs =>
    dayjs(
        time,
        format,
        strict,
    );

export const formatDateForForms = (time?: string | Dayjs | Date): string =>
    dayjs(time)
        .format(dateTimeFormatFormKit);

/**
 * Checks if a date is today or in the past
 * which would make it overdue.
 * @param time string | dayjs | Date
 * @returns boolean
 */
export const isOverdue = (date: string | Dayjs | Date): boolean =>
    dayjs(date)
        .isSameOrBefore(dayjsNow());

/**
 * Check if date is the same or before dateToCompare
 * @param date
 * @param dateToCompare
 */
export const isDateSameOrBefore = (
    date: string | Dayjs | Date,
    dateToCompare: string | Dayjs | Date,
    granularity: OpUnitType | undefined = 'minute',
): boolean =>
    dayjs(date)
        .isSameOrBefore(dayjs(dateToCompare), granularity);

/**
 * Check if date is the same or after dateToCompare
 */
export const isDateSameOrAfter = (
    date: string | Dayjs | Date,
    dateToCompare: string | Dayjs | Date,
    granularity: OpUnitType | undefined = 'minute',
): boolean =>
    dayjs(date)
        .isSameOrAfter(dayjs(dateToCompare), granularity);

/**
 * Check if date is between firstDate and secondDate
 * @param date
 * @param firstDate
 * @param secondDate
 */
export const isDateBetween = (
    date: string | Dayjs | Date,
    firstDate: string | Dayjs | Date,
    secondDate: string | Dayjs | Date,
    granularity: OpUnitType | undefined = 'day',
): boolean =>
    dayjs(date)
        .isBetween(
            dayjs(firstDate), dayjs(secondDate), granularity,
        );

/**
 * Merge two dates into one string (i.e. could return 13-14 May, 24)
 * When given 13 and 14th of may as separate dates
 * @param startDate string | Dayjs | Date
 * @param endDate string | Dayjs | Date
 * @returns string
 */
export const mergeDays = (startDate: string | Dayjs | Date, endDate: string | Dayjs | Date): string => {
    const dayjsStartDate = dayjs(startDate);
    const dayjsEndDate = dayjs(endDate);

    if (dayjsStartDate.isSame(dayjsEndDate, 'year')) {
        if (dayjsStartDate.isSame(dayjsEndDate, 'month')) {
            return `${dayjsStartDate.date()} - ${dayjsEndDate.format(dateFormatPrettyMinimizedYear)}`;
        }

        return `${dayjsStartDate.format('DD MMM')} - ${dayjsEndDate.format(dateFormatPrettyMinimizedYear)}`;
    }

    return `${dayjsStartDate.format(dateFormatPrettyMinimizedYear)} - ${dayjsEndDate.format(dateFormatPrettyMinimizedYear)}`;
};

export const mergeDaysFull = (startDate: string | Dayjs | Date, endDate: string | Dayjs | Date): string => {
    const dayjsStartDate = dayjs(startDate);
    const dayjsEndDate = dayjs(endDate);

    if (dayjsStartDate.isSame(dayjsEndDate, 'year')) {
        if (dayjsStartDate.isSame(dayjsEndDate, 'month')) {
            return `${dayjsStartDate.format('DD')} - ${dayjsEndDate.format(dateFormatPrettyFullYear)}`;
        }

        return `${dayjsStartDate.format('DD MMM')} - ${dayjsEndDate.format(dateFormatPrettyFullYear)}`;
    }

    return `${dayjsStartDate.format(dateFormatPrettyFullYear)} - ${dayjsEndDate.format(dateFormatPrettyFullYear)}`;
};

/**
 * Get a number that represents the day of the week
 * @param date
 */
export const getWeekDay = (date: string | Dayjs | Date): number =>
    dayjs(date)
        .weekday();

/**
 * Return date as '12h30' string
 * @param date string | Dayjs | Date
 * @returns string
 */
export const getTimePerUnit = (date: string | Dayjs | Date): string =>
    `${dayjs(date)
        .hour()}h${dayjs(date)
        .minute()}`;

/**
 * Return if date is today
 * @param date string | Dayjs | Date
 * @returns boolean
 */
export const dateIsToday = (date: string | Dayjs | Date): boolean =>
    dayjs(date)
        .isToday();

/**
 * Return if date is yesterday
 * @param date string | Dayjs | Date
 * @returns boolean
 */
export const dateIsYesterday = (date: string | Dayjs | Date): boolean =>
    dayjs(date)
        .isYesterday();

/**
 * Return if date is tomorrow
 * @param date string | Dayjs | Date
 * @returns boolean
 */
export const dateIsTomorrow = (date: string | Dayjs | Date): boolean =>
    dayjs(date)
        .isTomorrow();

export const dateWeekOfYear = (date: string | Dayjs | Date, newWeek?: number): number | Dayjs => {
    if (newWeek) {
        return dayjs(date)
            .week(newWeek);
    }
    return dayjs(date)
        .week();
};

export const isValidDate = (
    date: string, format = dateFormatFormKit, strict = false,
): boolean =>
    dayjs(
        date, format, strict,
    )
        .isValid();

/**
 * Business day logic: A business day runs from 06:00 AM to 05:59 AM next day.
 * Times from 00:00 to 05:59 belong to the previous business day.
 * Times from 06:00 to 23:59 belong to the current business day.
 */
export const BUSINESS_DAY_START_HOUR = 6;

/**
 * Determines the business day date for a given datetime.
 * Business day runs from 06:00 AM to 05:59 AM next day.
 * @param dateTime - The datetime to check
 * @returns The business day date as a Dayjs object
 */
export const getBusinessDayDate = (dateTime: string | Dayjs | Date): Dayjs => {
    const dt = dayjsObject(dateTime);
    const hour = dt.hour();

    // If time is between 00:00 and 05:59, it belongs to the previous business day
    if (hour < BUSINESS_DAY_START_HOUR) {
        return dt.subtract(1, 'day');
    }

    // Otherwise, it belongs to the current calendar day
    return dt;
};

/**
 * Gets the business day date formatted for FormKit
 * @param dateTime - The datetime to convert
 * @returns The business day date formatted as YYYY-MM-DD
 */
export const getBusinessDayFormatted = (dateTime: string | Dayjs | Date): string =>
    getBusinessDayDate(dateTime)
        .format(dateFormatFormKit);

/**
 * Checks if a datetime falls within the early hours (00:00-05:59)
 * that belong to the previous business day
 * @param dateTime - The datetime to check
 * @returns True if the time is in early hours (belongs to previous business day)
 */
export const isEarlyHours = (dateTime: string | Dayjs | Date): boolean => {
    const dt = dayjsObject(dateTime);
    return dt.hour() < BUSINESS_DAY_START_HOUR;
};

/**
 * Calculates the correct end datetime based on start date, start time, and end time.
 * Handles edge cases like crossing midnight, 24-hour slots, and multi-day scenarios.
 * @param {string} startDate - Format: 'YYYY-MM-DD'
 * @param {string} startTime - Format: 'HH:mm'
 * @param {string} endTime - Format: 'HH:mm'
 * @returns {{ startDateTime: string, endDateTime: string, durationMinutes: number }}
 */
// eslint-disable-next-line max-lines-per-function, max-statements
export const getEndDateTime = (
    startDate: string,
    startTime: string,
    endTime: string,
): {
    startDateTime: string;
    endDateTime: string;
    durationMinutes: number;
} => {
    // Validate input formats
    if (!/^\d{4}-\d{2}-\d{2}$/u.test(startDate)) {
        throw new Error(`Invalid format: ${startDate}. Expected: YYYY-MM-DD`);
    }
    if (!/^\d{2}:\d{2}$/u.test(startTime)) {
        throw new Error(`Invalid format: ${startTime}. Expected: HH:mm`);
    }
    if (!/^\d{2}:\d{2}$/u.test(endTime)) {
        throw new Error(`Invalid format: ${endTime}. Expected: HH:mm`);
    }

    const toMinutes = (time: string): number => {
        const parts = time.split(':')
            .map(Number);
        return (parts[0] * 60) + parts[1];
    };

    const startMins = toMinutes(startTime);
    const endMins = toMinutes(endTime);
    const isMidnight = startTime === '00:00' && endTime === '00:00';
    const addDay = !isMidnight && (endMins < startMins || (endMins === startMins && startMins !== 0));

    const base = dayjs(startDate, 'YYYY-MM-DD');
    const startDT = base.hour(Math.floor(startMins / 60))
        .minute(startMins % 60);
    const endDT = base.hour(Math.floor(endMins / 60))
        .minute(endMins % 60)
        .add(addDay ? 1 : 0, 'day');

    return {
        startDateTime: startDT.format(dateTimeFormatFormKit),
        endDateTime: endDT.format(dateTimeFormatFormKit),
        durationMinutes: endDT.diff(startDT, 'minute'),
    };
};

/**
 * Business day aware date/time calculation that handles the venue's business day logic.
 * Business day runs from 06:00 AM to 05:59 AM next day with restrictions:
 * - Date can only be current day or next day (in production)
 * - Start times from 00:00 to 05:59 belong to the next day (part of previous business day)
 * - Start times from 06:00 to 23:59 belong to the current day
 * - End time uses normal logic (no business day adjustment)
 * @param {string} currentTimeslotDate - Current timeslot date in YYYY-MM-DD format
 * @param {string} startTime - Format: 'HH:mm'
 * @param {string} endTime - Format: 'HH:mm'
 * @param {boolean} skipDateValidation - Skip date validation for testing purposes
 * @returns {{ startDateTime: string, endDateTime: string, durationMinutes: number }}
 */
// eslint-disable-next-line max-lines-per-function, max-statements, complexity
export const getBusinessDayAwareDateTime = (
    currentTimeslotDate: string,
    startTime: string,
    endTime: string,
    skipDateValidation = false,
): {
    startDateTime: string;
    endDateTime: string;
    durationMinutes: number;
} => {
    // Validate input formats
    if (!/^\d{4}-\d{2}-\d{2}$/u.test(currentTimeslotDate)) {
        throw new Error(`Invalid format: ${currentTimeslotDate}. Expected: YYYY-MM-DD`);
    }
    if (!/^\d{2}:\d{2}$/u.test(startTime)) {
        throw new Error(`Invalid format: ${startTime}. Expected: HH:mm`);
    }
    if (!/^\d{2}:\d{2}$/u.test(endTime)) {
        throw new Error(`Invalid format: ${endTime}. Expected: HH:mm`);
    }

    const toMinutes = (time: string): number => {
        const parts = time.split(':')
            .map(Number);
        return (parts[0] * 60) + parts[1];
    };

    const startMins = toMinutes(startTime);
    const endMins = toMinutes(endTime);
    const businessDayStartMins = BUSINESS_DAY_START_HOUR * 60; // 06:00 = 360 minutes

    const currentDate = dayjsObject(currentTimeslotDate);

    // Restrict dates to only current day or next day (skip in tests)
    if (!skipDateValidation) {
        const today = dayjsNow()
            .startOf('day');
        const tomorrow = today.add(1, 'day');
        const isValidCurrentDate = currentDate.isSame(today, 'day') || currentDate.isSame(tomorrow, 'day');
        if (!isValidCurrentDate) {
            throw new Error('Date must be current day or next day only');
        }
    }

    // Determine the actual calendar date for start time based on business day logic
    // eslint-disable-next-line @typescript-eslint/init-declarations
    let startCalendarDate: string;

    // For start time: if it's early hours (00:00-05:59), it goes to next calendar day
    if (startMins < businessDayStartMins) {
        // Early hours belong to next calendar day
        startCalendarDate = currentDate.add(1, 'day')
            .format('YYYY-MM-DD');
    } else {
        // Normal hours belong to current calendar day
        startCalendarDate = currentDate.format('YYYY-MM-DD');
    }

    // Create start datetime
    const startDateTime = dayjsObject(`${startCalendarDate} ${startTime}`);

    // For end time: use normal logic (same date as start date, then adjust if needed)
    let endDateTime = dayjsObject(`${startCalendarDate} ${endTime}`);

    // If end time is before start time, it means it crosses to the next day
    if (endDateTime.isBefore(startDateTime) || endDateTime.isSame(startDateTime)) {
        if (endMins === startMins && startMins !== 0) {
            // Same time = 24-hour slot
            endDateTime = startDateTime.add(1, 'day');
        } else if (endDateTime.isBefore(startDateTime)) {
            // End time is earlier than start time, so it's next day
            endDateTime = endDateTime.add(1, 'day');
        }
    }

    // Validate end date is within allowed range (current day or next day)
    if (!skipDateValidation) {
        const today = dayjsNow()
            .startOf('day');
        const tomorrow = today.add(1, 'day');
        const endDate = endDateTime.startOf('day');
        const isEndDateValid = endDate.isSame(today, 'day') || endDate.isSame(tomorrow, 'day');
        if (!isEndDateValid) {
            throw new Error('End date must be current day or next day only');
        }
    }

    return {
        startDateTime: startDateTime.format(dateTimeFormatFormKit),
        endDateTime: endDateTime.format(dateTimeFormatFormKit),
        durationMinutes: endDateTime.diff(startDateTime, 'minute'),
    };
};

/**
 * Date/time calculation for editing existing timeslots.
 * Resolves the business day date from the current start datetime, then re-applies
 * the business day rule to the new start time:
 * - New start time 00:00–05:59 (early hours) → calendar date is business day + 1 day
 * - New start time 06:00–23:59 (normal hours) → calendar date is the business day date
 * This prevents a timeslot that was in early hours from staying on the "next day"
 * when the user moves it to a normal hour, and vice versa.
 * @param {string} currentStartDateTime - Current start datetime in format YYYY-MM-DD HH:mm
 * @param {string} startTime - New start time in format HH:mm
 * @param {string} endTime - New end time in format HH:mm
 * @returns {{ startDateTime: string, endDateTime: string, durationMinutes: number }}
 */
export const getDateTimeForEditing = (
    currentStartDateTime: string,
    startTime: string,
    endTime: string,
): {
    startDateTime: string;
    endDateTime: string;
    durationMinutes: number;
} => {
    const toMinutes = (time: string): number => {
        const parts = time.split(':')
            .map(Number);
        return (parts[0] * 60) + parts[1];
    };

    const businessDayStartMins = BUSINESS_DAY_START_HOUR * 60;
    const newStartMins = toMinutes(startTime);

    // Determine the business day date from the current start datetime
    const businessDayDate = getBusinessDayDate(currentStartDateTime);

    // Re-apply business day rule: early hours go to next calendar day, normal hours stay on business day date
    const startDate = newStartMins < businessDayStartMins
        ? businessDayDate.add(1, 'day')
            .format('YYYY-MM-DD')
        : businessDayDate.format('YYYY-MM-DD');

    return getEndDateTime(
        startDate, startTime, endTime,
    );
};

/**
     * Get a year string based on offset from current year
     * @param offset - Number of years to add (positive) or subtract (negative) from current year
     * @returns Year as string in YYYY format
     */
export const getYear = (offset: number): string => dayjsNow()
    .add(offset, 'year')
    .format('YYYY');
